import {
  addDomainRequestSchema,
  canIssueCertificate,
  DNS_SETUP_KINDS,
  DOMAIN_STATUSES,
  MAX_CUSTOM_DOMAINS_PER_STORE,
  type AddDomainRequest,
  type DnsSetupKind,
  type DnsSetupOption,
  type Domain,
  type DomainDnsInstructions,
  type DomainReason,
  type DomainStatus,
  type PlatformDomainsResponse,
} from '@avrahami/contracts';
import { fill, type Parsed } from '@avrahami/ui';
import { he } from '../i18n/he';
import { parseWith } from './forms';

/**
 * The stores' own domains, as the platform admin watches them (docs/DOMAINS.md): which need
 * attention, and whose certificate is about to end.
 */

/** How often the monitoring list is read again. */
export const DOMAINS_POLL_MS = 60_000;

/** The statuses as the screen lists them: the ones that need attention first. */
export const DOMAIN_STATUS_ORDER = [
  'DNS_LOST',
  'FAILED',
  'PENDING',
  'VERIFYING',
  'ACTIVE',
  'DISABLED',
] as const satisfies readonly DomainStatus[];

/** How many own domains the stores hold, in every status together. */
export function totalDomains(counts: PlatformDomainsResponse['counts']): number {
  return DOMAIN_STATUSES.reduce((sum, status) => sum + counts[status], 0);
}

/** From this many days before its end on, a certificate is marked: renewal should have happened. */
export const CERTIFICATE_WARNING_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * - NONE: no probe saw a certificate yet
 * - OK: valid for longer than the warning period
 * - ENDING: ends within the warning period
 * - ENDED: its end has passed
 */
export type CertificateState = 'NONE' | 'OK' | 'ENDING' | 'ENDED';

export function certificateState(tlsValidUntil: string | null, now: number): CertificateState {
  if (tlsValidUntil === null) return 'NONE';
  const end = Date.parse(tlsValidUntil);
  if (Number.isNaN(end)) return 'NONE';
  if (end <= now) return 'ENDED';
  return end - now <= CERTIFICATE_WARNING_DAYS * DAY_MS ? 'ENDING' : 'OK';
}

/** The system address is always there; only an own domain has a state worth naming. */
export function domainChip(domain: Pick<Domain, 'kind' | 'status'>): DomainStatus | 'SYSTEM' {
  return domain.kind === 'SYSTEM' ? 'SYSTEM' : domain.status;
}

/**
 * One store's addresses, as the platform staff connect them (docs/DOMAINS.md, adr/0025): what an
 * address may be asked to do, what its state means in words, and when the screen keeps reading.
 * The screen itself only presents.
 */

type DomainState = Pick<Domain, 'kind' | 'status'>;

/**
 * An address as people type or paste it ("https://WWW.MyShop.co.il/about") as the hostname the
 * API takes: no scheme, no path, lower-case, no trailing dot.
 */
export function normalizeHostnameInput(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/\.+$/, '');
}

/** The request that adds an address, checked with the contract the API validates it with. */
export function addDomainRequest(text: string): Parsed<AddDomainRequest> {
  const hostname = normalizeHostnameInput(text);
  if (hostname === '') return { ok: false, errors: { hostname: he.common.required } };
  return parseWith(addDomainRequestSchema, { hostname });
}

/** The system address comes first; the store's own addresses follow in the order they were added. */
export function sortDomains(domains: readonly Domain[]): Domain[] {
  return [...domains].sort(
    (a, b) =>
      Number(b.kind === 'SYSTEM') - Number(a.kind === 'SYSTEM') ||
      a.createdAt.localeCompare(b.createdAt) ||
      a.hostname.localeCompare(b.hostname),
  );
}

/** The store reached the number of own addresses it may hold. */
export function domainsFull(domains: readonly Pick<Domain, 'kind'>[]): boolean {
  const own = domains.filter((domain) => domain.kind === 'CUSTOM').length;
  return own >= MAX_CUSTOM_DOMAINS_PER_STORE;
}

/** An address is still being checked: the screen follows it until the checks end. */
export function domainInProgress(domain: DomainState): boolean {
  return domain.kind === 'CUSTOM' && (domain.status === 'PENDING' || domain.status === 'VERIFYING');
}

export function needsPolling(domains: readonly DomainState[] | undefined): boolean {
  return domains?.some(domainInProgress) ?? false;
}

/** Only an address that serves the store can be the one shoppers are sent to. */
export function canMakePrimary(domain: Pick<Domain, 'status' | 'isPrimary'>): boolean {
  return domain.status === 'ACTIVE' && !domain.isPrimary;
}

/** The system address needs no checks, and one the platform switched off gets none. */
export function canCheckNow(domain: DomainState): boolean {
  return domain.kind === 'CUSTOM' && domain.status !== 'DISABLED';
}

export function canRemove(domain: Pick<Domain, 'kind'>): boolean {
  return domain.kind === 'CUSTOM';
}

/** The DNS records are shown while the address does not serve the store (also after it did). */
export function needsSetup(domain: DomainState): boolean {
  return (
    domain.kind === 'CUSTOM' &&
    (domain.status === 'PENDING' ||
      domain.status === 'VERIFYING' ||
      domain.status === 'FAILED' ||
      domain.status === 'DNS_LOST')
  );
}

/**
 * The list saw a later check of an address than its open card did (the list is followed while
 * any address is being checked; a card only while its own address is).
 */
export function detailBehind(
  listed: Pick<Domain, 'lastCheckedAt'>,
  detailed: Pick<Domain, 'lastCheckedAt'>,
): boolean {
  if (listed.lastCheckedAt === null) return false;
  if (detailed.lastCheckedAt === null) return true;
  return Date.parse(listed.lastCheckedAt) > Date.parse(detailed.lastCheckedAt);
}

/** The address whose details the screen opens: the chosen one, or the first that needs work. */
export function openDomain(
  domains: readonly Domain[],
  chosenId: string | undefined,
): Domain | undefined {
  return (
    domains.find((domain) => domain.id === chosenId && domain.kind === 'CUSTOM') ??
    domains.find(needsSetup)
  );
}

export function setupOption(
  dns: DomainDnsInstructions,
  kind: DnsSetupKind,
): DnsSetupOption | undefined {
  return dns.options.find((option) => option.kind === kind);
}

/** Where the CNAME record points: the store's system address. */
export function cnameTarget(dns: DomainDnsInstructions | null): string | undefined {
  if (dns === null) return undefined;
  return setupOption(dns, 'CNAME')?.records.find((record) => record.type === 'CNAME')?.value;
}

/**
 * What the screen says about an address in a state. Where the staff issue the certificate with a
 * button (adr/0029), an address that waits for it is told so, and an active one whose
 * certificate is not accepted is offered a new one.
 */
export function statusText(
  domain: Pick<Domain, 'kind' | 'status' | 'reason'>,
  certificateIssuing: boolean,
): string {
  if (certificateIssuing && canIssueCertificate(domain)) {
    return domain.status === 'VERIFYING' ? he.storeDomains.issueReady : he.storeDomains.issueAgain;
  }
  return he.storeDomains.statusText[domain.status];
}

/** While the certificate is on its way, the state's own text already says so. */
export function showsReason(domain: Pick<Domain, 'status' | 'reason'>): boolean {
  if (domain.reason === null) return false;
  return !(domain.status === 'VERIFYING' && domain.reason === 'TLS_ERROR');
}

/** What is missing, as a sentence that says what to do about it. */
export function reasonText(reason: DomainReason, dns: DomainDnsInstructions | null): string {
  if (reason !== 'WRONG_TARGET') return he.storeDomains.reasons[reason];
  const target = cnameTarget(dns);
  return target === undefined
    ? he.storeDomains.reasons.WRONG_TARGET
    : fill(he.storeDomains.wrongTargetTo, { target });
}

/**
 * Each address is added by itself: `www.shop.co.il` does not cover `shop.co.il`. The other form
 * of an address, when the store does not have it yet.
 */
export function missingTwin(
  hostname: string,
  domains: readonly Pick<Domain, 'hostname'>[],
): string | undefined {
  const twin = hostname.startsWith('www.') ? hostname.slice(4) : `www.${hostname}`;
  // "www.co" has no twin: what is left must still be a domain.
  if (!twin.includes('.')) return undefined;
  return domains.some((domain) => domain.hostname === twin) ? undefined : twin;
}

/**
 * Endings under which domains are registered one level deeper (`shop.co.il`, not `co.il`). Not
 * the whole public suffix list: the stores' domains are Israeli, and these are the common ones.
 */
const SECOND_LEVEL_SUFFIXES = new Set([
  'co.il',
  'org.il',
  'net.il',
  'ac.il',
  'gov.il',
  'muni.il',
  'k12.il',
  'idf.il',
  'co.uk',
  'org.uk',
  'com.au',
  'net.au',
  'com.br',
  'co.za',
  'com.tr',
  'co.nz',
]);

/**
 * The domain as it is registered, which a DNS provider's screen manages and appends to every
 * record name: `www.shop.co.il` → `shop.co.il`.
 */
export function dnsZone(hostname: string): string {
  const labels = hostname.split('.');
  const lastTwo = labels.slice(-2).join('.');
  return labels.slice(SECOND_LEVEL_SUFFIXES.has(lastTwo) ? -3 : -2).join('.');
}

/** The domain itself, with no prefix (`shop.co.il`): DNS allows no CNAME record there. */
export function isRootAddress(hostname: string): boolean {
  return dnsZone(hostname) === hostname;
}

/** The ways to connect an address that the screen offers: a root address has no CNAME way. */
export function setupKinds(hostname: string): readonly DnsSetupKind[] {
  return isRootAddress(hostname) ? ['A_TXT'] : DNS_SETUP_KINDS;
}

/**
 * What goes in the provider's "Name" / "Host" field: the record's name without the zone, which
 * the provider adds by itself; `@` for the zone itself.
 */
export function recordHost(name: string, zone: string): string {
  if (name === zone) return '@';
  return name.endsWith(`.${zone}`) ? name.slice(0, -(zone.length + 1)) : name;
}

/**
 * An address as people read it: the link without its scheme. A system address is not always a
 * hostname of its own (it can be a path of a shared host), so the screen shows the link the API
 * gives, never one built from the hostname.
 */
export function addressText(url: string): string {
  return url.replace(/^https:\/\//, '');
}
